// src/components/Visualization/DistributionPie.js
import React, { useState } from 'react';
import { 
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, 
  Legend, Sector, Label
} from 'recharts';

/**
 * DistributionPie - Gráfico de Pizza/Rosca avançado para exibir distribuições
 * 
 * @param {Object} props
 * @param {Array} props.data - Array de dados para o gráfico
 * @param {string} props.nameKey - Chave para os nomes das fatias
 * @param {string} props.dataKey - Chave para os valores das fatias
 * @param {string} props.title - Título do gráfico
 * @param {boolean} props.donut - Mostrar como gráfico de rosca
 * @param {boolean} props.showLegend - Exibir legenda
 * @param {boolean} props.activeOnHover - Destacar fatia ao passar o mouse
 * @param {boolean} props.showLabels - Mostrar rótulos nas fatias
 * @param {string} props.innerRadius - Raio interno para gráfico de rosca
 * @param {string} props.outerRadius - Raio externo do gráfico
 * @param {Function} props.valueFormatter - Formatar valores
 * @param {Array} props.colors - Cores personalizadas para as fatias
 * @param {string} props.centerLabel - Texto a ser exibido no centro de um gráfico de rosca
 * @param {string} props.centerValue - Valor a ser exibido no centro de um gráfico de rosca
 * @param {Object} props.tooltipFormatter - Função personalizada para tooltip
 * @param {number} props.paddingAngle - Ângulo de espaçamento entre fatias
 */
const DistributionPie = ({
  data = [],
  nameKey = 'name',
  dataKey = 'value',
  title = '',
  donut = false,
  showLegend = true,
  activeOnHover = true,
  showLabels = true,
  innerRadius = "60%",
  outerRadius = "80%",
  valueFormatter = (value) => value,
  colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#D0ED57', '#ff7300', '#F44336'],
  centerLabel = '',
  centerValue = '',
  tooltipFormatter,
  paddingAngle = 2
}) => {
  // Estado para controlar a fatia ativa (ao passar o mouse)
  const [activeIndex, setActiveIndex] = useState(null);

  // Calcula o total para percentuais
  const total = data.reduce((sum, item) => sum + item[dataKey], 0);

  // Função para renderizar fatia ativa
  const renderActiveShape = (props) => {
    const {
      cx, cy, innerRadius, outerRadius, startAngle, endAngle,
      fill, payload, percent
    } = props;

    // Calcula percentual para exibição
    const percentValue = `${(percent * 100).toFixed(1)}%`;
    
    // Valor formatado usando a função personalizada
    const formattedValue = valueFormatter(payload[dataKey]);

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 6} // Aumenta o tamanho da fatia ativa
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 8}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
        {showLabels && (
          <text
            x={cx}
            y={cy - 15}
            textAnchor="middle"
            fill="#333"
            className="text-sm font-medium"
          >
            {payload[nameKey]}
          </text>
        )}
        {showLabels && (
          <text
            x={cx}
            y={cy + 5}
            textAnchor="middle"
            fill="#333"
            className="text-xs"
          >
            {formattedValue}
          </text>
        )}
        {showLabels && (
          <text
            x={cx}
            y={cy + 20}
            textAnchor="middle"
            fill="#666"
            className="text-xs"
          >
            {percentValue}
          </text>
        )}
      </g>
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentValue = ((data.value / total) * 100).toFixed(1);
      
      return (
        <div className="bg-white p-3 shadow-md border border-gray-200 rounded-md">
          <p className="font-medium text-sm">{data.name}</p>
          <p className="text-xs text-gray-700 mt-1">
            {valueFormatter(data.value)} ({percentValue}%)
          </p>
        </div>
      );
    }

    return null;
  };

  // Handlers para a interatividade
  const handleMouseEnter = (_, index) => {
    if (activeOnHover) {
      setActiveIndex(index);
    }
  };

  const handleMouseLeave = () => {
    if (activeOnHover) {
      setActiveIndex(null);
    }
  };

  return (
    <div className="h-full w-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            activeIndex={activeIndex}
            activeShape={activeOnHover ? renderActiveShape : null}
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={donut ? innerRadius : 0}
            outerRadius={outerRadius}
            nameKey={nameKey}
            dataKey={dataKey}
            paddingAngle={paddingAngle}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            isAnimationActive={true}
            animationDuration={800}
          >
            {/* Rótulos para as fatias */}
            {showLabels && !activeOnHover && (
              <Label
                position="inside"
                content={({ viewBox }) => {
                  const { cx, cy } = viewBox;
                  return (
                    <g>
                      {centerLabel && (
                        <text
                          x={cx}
                          y={cy - 10}
                          textAnchor="middle"
                          fill="#666"
                          fontSize={12}
                          fontWeight={500}
                        >
                          {centerLabel}
                        </text>
                      )}
                      {centerValue && (
                        <text
                          x={cx}
                          y={cy + 15}
                          textAnchor="middle"
                          fill="#333"
                          fontSize={20}
                          fontWeight={600}
                        >
                          {centerValue}
                        </text>
                      )}
                    </g>
                  );
                }}
              />
            )}

            {/* Cores para as fatias */}
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={colors[index % colors.length]} 
              />
            ))}
          </Pie>

          {/* Tooltip personalizado */}
          <Tooltip content={<CustomTooltip />} />

          {/* Legenda */}
          {showLegend && (
            <Legend 
              layout="horizontal" 
              verticalAlign="bottom" 
              align="center"
              wrapperStyle={{ fontSize: 12, bottom: 0 }}
              iconType="circle"
              iconSize={8}
            />
          )}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DistributionPie;
